// src/components/core/WeatherWidget.jsx
// Weather widget that displays cached weather data from Supabase
// Data is populated by update-weather Edge Function running every 2 hours
// See: /supabase/migrations/# OurNet Weather Caching Implementation .md

import React, { useEffect, useState } from 'react';
import { useTheme } from '../../themes/ThemeProvider';
import { supabaseFetch } from '../../lib/supabase';
import { useTranslation } from '../../i18n/useTranslation';
import {
  Sun,
  Cloud,
  CloudRain,
  CloudSnow,
  CloudLightning,
  CloudFog,
  Wind,
  Droplets,
  Thermometer,
  CloudDrizzle,
  Cloudy
} from 'lucide-react';

const WeatherWidget = ({
  locationName = null,
  locationCoords = null, // { lat, lon } from user's home area
  compact = false, // When true: hide description, stats, timestamp; reduce icon+temp sizes
  hideLocationName = false // When true: suppress the widget's own place-name label (a caller
                            // that already shows the locality name elsewhere — e.g. the desktop
                            // front page's Directory headline — sets this to avoid duplication)
}) => {
  const theme = useTheme();
  const { t } = useTranslation('common');
  const [weather, setWeather] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    const fetchWeather = async () => {
      if (!locationCoords?.lat || !locationCoords?.lon) {
        setLoading(false);
        setError('No location available');
        return;
      }

      try {
        // Query weather_cache for the nearest locality by coordinates
        // Match by finding the locality with matching lat/lon (within tolerance)
        const { data: weatherData, error: fetchError } = await supabaseFetch.query('weather_cache', {
          select: '*',
          order: 'fetched_at.desc',
          limit: 1
        });

        if (fetchError) {
          console.error('Weather fetch error:', fetchError);
          setError('Unable to load weather');
          setLoading(false);
          return;
        }

        // If we have cached data, find the closest match to our coordinates
        if (weatherData && weatherData.length > 0) {
          // Query all weather cache entries and find closest to user coords
          const { data: allWeather } = await supabaseFetch.query('weather_cache', {
            select: '*'
          });

          if (allWeather && allWeather.length > 0) {
            // Find the closest locality by calculating distance
            let closest = allWeather[0];
            let minDistance = calculateDistance(
              locationCoords.lat,
              locationCoords.lon,
              closest.latitude,
              closest.longitude
            );

            for (const w of allWeather) {
              const dist = calculateDistance(
                locationCoords.lat,
                locationCoords.lon,
                w.latitude,
                w.longitude
              );
              if (dist < minDistance) {
                minDistance = dist;
                closest = w;
              }
            }

            setWeather(closest);
          } else {
            setError('No weather data available');
          }
        } else {
          setError('No weather data available');
        }
      } catch (err) {
        console.error('Weather fetch error:', err);
        setError('Unable to load weather');
      } finally {
        setLoading(false);
      }
    };

    fetchWeather();
  }, [locationCoords]);

  // Calculate distance between two coordinates (Haversine formula simplified)
  const calculateDistance = (lat1, lon1, lat2, lon2) => {
    const R = 6371; // Earth's radius in km
    const dLat = (lat2 - lat1) * Math.PI / 180;
    const dLon = (lon2 - lon1) * Math.PI / 180;
    const a =
      Math.sin(dLat/2) * Math.sin(dLat/2) +
      Math.cos(lat1 * Math.PI / 180) * Math.cos(lat2 * Math.PI / 180) *
      Math.sin(dLon/2) * Math.sin(dLon/2);
    const c = 2 * Math.atan2(Math.sqrt(a), Math.sqrt(1-a));
    return R * c;
  };

  // Get weather icon component based on OpenWeatherMap icon code
  const getWeatherIcon = (iconCode, size = 48) => {
    if (!iconCode) return <Sun size={size} />;

    // OpenWeatherMap icon codes: https://openweathermap.org/weather-conditions
    // Format: XXd or XXn (d=day, n=night)
    const code = iconCode.substring(0, 2);
    const iconProps = { size, strokeWidth: 1.5 };

    switch (code) {
      case '01': // Clear sky
        return <Sun {...iconProps} />;
      case '02': // Few clouds
        return <Cloud {...iconProps} />;
      case '03': // Scattered clouds
        return <Cloud {...iconProps} />;
      case '04': // Broken/overcast clouds
        return <Cloudy {...iconProps} />;
      case '09': // Shower rain
        return <CloudDrizzle {...iconProps} />;
      case '10': // Rain
        return <CloudRain {...iconProps} />;
      case '11': // Thunderstorm
        return <CloudLightning {...iconProps} />;
      case '13': // Snow
        return <CloudSnow {...iconProps} />;
      case '50': // Mist/fog
        return <CloudFog {...iconProps} />;
      default:
        return <Sun {...iconProps} />;
    }
  };

  // Format "Updated X mins/hours ago"
  const getTimeAgo = (timestamp) => {
    if (!timestamp) return '';

    const now = new Date();
    const fetchedAt = new Date(timestamp);
    const diffMs = now - fetchedAt;
    const diffMins = Math.floor(diffMs / 60000);
    const diffHours = Math.floor(diffMins / 60);
    const diffDays = Math.floor(diffHours / 24);

    if (diffMins < 1) return 'Updated just now';
    if (diffMins < 60) return `Updated ${diffMins} min${diffMins === 1 ? '' : 's'} ago`;
    if (diffHours < 24) return `Updated ${diffHours} hour${diffHours === 1 ? '' : 's'} ago`;
    return `Updated ${diffDays} day${diffDays === 1 ? '' : 's'} ago`;
  };

  // Loading state
  if (loading) {
    return (
      <div style={{
        width: '100%',
        height: '100%',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        fontFamily: 'OpenSauceSans, sans-serif',
        color: theme?.primary || '#024B99'
      }}>
        <div style={{ textAlign: 'center', opacity: 0.7 }}>
          <Cloud size={32} style={{ animation: 'pulse 1.5s infinite' }} />
          <div style={{ marginTop: '8px', fontSize: '12px' }}>{t('weather_loading')}</div>
        </div>
      </div>
    );
  }

  // Error state
  if (error || !weather) {
    return (
      <div style={{
        width: '100%',
        height: '100%',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        fontFamily: 'OpenSauceSans, sans-serif',
        color: '#6c757d',
        textAlign: 'center',
        padding: '16px'
      }}>
        <div>
          <Cloud size={32} style={{ opacity: 0.5 }} />
          <div style={{ marginTop: '8px', fontSize: '12px' }}>
            {t('weather_unavailable')}
          </div>
        </div>
      </div>
    );
  }

  // Main weather display
  return (
    <div style={{
      width: '100%',
      height: 'auto',
      display: 'flex',
      flexDirection: 'column',
      alignItems: 'flex-start',
      justifyContent: 'flex-start',
      fontFamily: 'OpenSauceSans, sans-serif',
      color: theme?.primary || '#024B99',
      padding: compact ? '4px 8px 2px 16px' : '6px 12px 4px 20px'
    }}>
      {/* Location name — suppressed when a caller already shows it elsewhere (hideLocationName) */}
      {!hideLocationName && (
        <div style={{
          fontSize: compact ? '12px' : '14px',
          fontWeight: '700',
          textTransform: 'uppercase',
          letterSpacing: '0.5px',
          marginBottom: compact ? '2px' : '4px',
          textAlign: 'left'
        }}>
          {locationName || weather.locality_name || t('weather_local')}
        </div>
      )}

      {/* Main weather display: icon + temperature */}
      <div style={{
        display: 'flex',
        alignItems: 'center',
        gap: compact ? '8px' : '12px',
        marginBottom: compact ? '0' : '4px'
      }}>
        <div style={{ color: theme?.primary || '#024B99' }}>
          {getWeatherIcon(weather.weather_icon, compact ? 32 : undefined)}
        </div>
        <div style={{
          fontSize: compact ? '28px' : '42px',
          fontWeight: '800',
          lineHeight: 1
        }}>
          {Math.round(weather.temperature)}°
        </div>
      </div>

      {/* Weather description — hidden in compact mode */}
      {!compact && (
        <div style={{
          fontSize: '14px',
          fontWeight: '500',
          textTransform: 'capitalize',
          marginBottom: '6px',
          opacity: 0.9
        }}>
          {weather.weather_description}
        </div>
      )}

      {/* Stats row: feels like, humidity, wind — hidden in compact mode */}
      {!compact && (
        <div style={{
          display: 'flex',
          gap: '16px',
          fontSize: '12px',
          opacity: 0.8
        }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: '4px' }}>
            <Thermometer size={14} />
            <span>Feels {Math.round(weather.feels_like)}°</span>
          </div>
          <div style={{ display: 'flex', alignItems: 'center', gap: '4px' }}>
            <Droplets size={14} />
            <span>{weather.humidity}%</span>
          </div>
          <div style={{ display: 'flex', alignItems: 'center', gap: '4px' }}>
            <Wind size={14} />
            <span>{weather.wind_speed} km/h</span>
          </div>
        </div>
      )}

      {/* Updated timestamp — hidden in compact mode */}
      {!compact && (
        <div style={{
          marginTop: '6px',
          fontSize: '10px',
          opacity: 0.5,
          fontStyle: 'italic'
        }}>
          {getTimeAgo(weather.fetched_at)}
        </div>
      )}
    </div>
  );
};

export default WeatherWidget;
